import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import type { SessionCapabilities } from "@shared/types";
import {
  supabase,
  clearStoredSession,
  getSessionSafe,
  sessionUncertain,
  signOutSafe,
  waitForSession,
} from "@/lib/supabase";
import { loginPathFor } from "@shared/loginPath";
import { api } from "@/lib/api";
import { clearApiCache } from "@/lib/apiCache";
import { setImageOptimizeConfig } from "@/lib/imageOptimize";
import { isStorefrontHost } from "@/lib/host";

export type UserRole = "seller" | "customer";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** True when a user is signed in AND their email is verified. */
  isVerified: boolean;
  /** Upgraded to a seller (`profiles.is_seller`) — gates /dashboard. */
  isSeller: boolean;
  /** Bought at least once (`profiles.has_purchased`) — gates /account. */
  hasPurchased: boolean;
  /** True when this account has `profiles.is_platform_admin` — gates /admin. */
  isPlatformAdmin: boolean;
  /** True when this account has `profiles.is_reseller` — gates /reseller. */
  isReseller: boolean;
  /** Holds an active affiliate account — opens /affiliate. */
  isAffiliate: boolean;
  /** True until the capability probe has resolved. Every gate must wait on it:
      deciding on the pre-probe `false` sends a seller to /account and flashes
      "Naik taraf" at someone who already has a store. */
  capsLoading: boolean;
  /** Re-read the flags after an upgrade so /dashboard opens without a reload. */
  refreshCapabilities: () => Promise<void>;
  /**
   * Signs out THIS browser only unless `scope: "global"` is passed (the explicit
   * "sign out of all devices" control). `redirectTo` is an in-app path to land on
   * instead of the login page for the current route.
   */
  signOut: (opts?: { scope?: "global" | "local"; redirectTo?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * A stable fingerprint of *who* is signed in — user id, verification state,
 * role and display name (the header shows it, so a rename must get through). supabase-js re-emits auth events (SIGNED_IN / TOKEN_REFRESHED) on every
 * tab focus and background token refresh; the access token itself is read fresh
 * from getSession() per request (see api.ts), so React only ever needs the
 * identity. When the fingerprint is unchanged we keep the previous session
 * object so the context — and everything keyed off it — stays referentially
 * stable and the dashboard doesn't remount (which would wipe unsaved forms).
 */
function sessionIdentity(s: Session | null): string {
  const u = s?.user;
  if (!u) return "none";
  return `${u.id}:${u.email_confirmed_at ?? ""}:${u.user_metadata?.role ?? ""}:${u.user_metadata?.full_name ?? ""}`;
}

/**
 * What to believe when the capability probe cannot be reached. Ejecting the user
 * would be the worst possible answer to a network blip — a buyer thrown out of
 * their own library, a seller out of their dashboard — so we fall back to what
 * the JWT itself carries, which is exactly how the guards behaved before the
 * flags existed: the metadata role decides the seller side, and any verified
 * account is allowed into the buyer side.
 */
function offlineCaps(user: User | null): SessionCapabilities {
  return {
    email: user?.email ?? "",
    isSeller: user?.user_metadata?.role !== "customer",
    hasPurchased: true,
    isPlatformAdmin: false,
    isReseller: false,
    // Unknown, so no affiliate card: AffiliateRoute still lets a real affiliate in.
    isAffiliate: false,
  };
}

const BOOT_REFRESH_WAIT_MS = 15_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [caps, setCaps] = useState<SessionCapabilities | null>(null);
  const [capsLoading, setCapsLoading] = useState(true);

  useEffect(() => {
    // getSessionSafe (not the bare getSession) so a stalled token refresh can't
    // pin `loading` true forever — that would leave the app on its full-screen
    // spinner with no way in. A slow or network-failed refresh gets a bounded
    // second chance first: answering null on the first stall sent a signed-in
    // user back to /login just for opening a tab that had slept past its token.
    getSessionSafe()
      .then(async (next) => {
        if (next || !sessionUncertain()) return next;
        const late = await waitForSession(BOOT_REFRESH_WAIT_MS);
        // Still stuck after the whole wait: a poisoned token that would re-hang
        // /login on the next boot. Offline is different — keep that session.
        if (!late && sessionUncertain() === "stalled") clearStoredSession();
        return late;
      })
      .then((next) => {
        setSession(next);
        setLoading(false);
      });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      // Ignore no-op re-emits (same user, same verification/role): a bare token
      // refresh on tab switch must not churn the context. Only a real change in
      // identity — sign in/out, email verified, role change — updates state.
      setSession((prev) => {
        if (sessionIdentity(prev) === sessionIdentity(next)) return prev;
        // Another account (or none) in this browser: nothing remembered for the
        // previous one may show.
        if (prev?.user?.id !== next?.user?.id) clearApiCache();
        return next;
      });
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // One probe answers all three gates — which portals this account may open and
  // whether it is an operator. It replaces the old /admin/session call, which
  // sat behind the super-admin middleware and so 403'd for every normal user.
  const loadCapabilities = useCallback(async () => {
    try {
      const r = await api.get<SessionCapabilities>("/auth/session");
      setCaps({
        email: r.email ?? "",
        isSeller: Boolean(r.isSeller),
        hasPurchased: Boolean(r.hasPurchased),
        isPlatformAdmin: Boolean(r.isPlatformAdmin),
        isReseller: Boolean(r.isReseller),
        isAffiliate: Boolean(r.isAffiliate),
      });
    } catch {
      // Null means "we could not ask", NOT "the answer is no" — the difference
      // decides whether a real buyer keeps their portal when the network hiccups.
      setCaps(null);
    }
  }, []);

  useEffect(() => {
    // Wait until getSession() settles. Resolving the probe against the pre-load
    // null session drops capsLoading to false too early, so a hard refresh of a
    // gated route redirects before we know anything about the account.
    if (loading) return;
    // A seller storefront mounts neither portal, so it has no use for these
    // flags — and probing from there would be actively harmful: the old probe
    // answered 403 for a normal user (swallowed), this one answers 401 for a
    // stale token, which the API client turns into a redirect to /login on the
    // SELLER's host. Buyer auth is canonical on the apex; leave it there.
    if (isStorefrontHost()) {
      setCaps(null);
      setCapsLoading(false);
      return;
    }
    const user = session?.user ?? null;
    if (!user?.email_confirmed_at) {
      setCaps(null);
      setCapsLoading(false);
      return;
    }
    let active = true;
    setCapsLoading(true);
    loadCapabilities().finally(() => active && setCapsLoading(false));
    return () => {
      active = false;
    };
  }, [session, loading, loadCapabilities]);

  // Client-side WebP conversion (resources.ts) is ON for every seller: R2 was
  // filling with raw multi-megabyte PNGs, and the optimizer already falls back
  // to the untouched original on any decode/encode trouble. Ordinary sellers get
  // it unconditionally — the "Optimize Gambar" card is still platform-admin only,
  // so their user_settings row can't have been turned off, and not reading it
  // saves a request and keeps the default from drifting. Platform admins keep
  // honouring their own toggles so the feature stays testable from the UI.
  useEffect(() => {
    if (capsLoading) return;
    if (!caps?.isPlatformAdmin) {
      setImageOptimizeConfig({ webp: true, compress: true });
      return;
    }
    let active = true;
    api
      .get<{ optimize_images_webp: boolean; optimize_images_compress: boolean }>("/settings")
      .then((s) => {
        if (!active) return;
        setImageOptimizeConfig({
          webp: Boolean(s.optimize_images_webp),
          compress: Boolean(s.optimize_images_compress),
        });
      })
      .catch(() => active && setImageOptimizeConfig({ webp: true, compress: true }));
    return () => {
      active = false;
    };
  }, [caps?.isPlatformAdmin, capsLoading]);

  const value = useMemo<AuthContextValue>(() => {
    const user = session?.user ?? null;
    const effective = caps ?? offlineCaps(user);
    return {
      session,
      user,
      loading,
      isVerified: Boolean(user?.email_confirmed_at),
      isSeller: effective.isSeller,
      hasPurchased: effective.hasPurchased,
      isPlatformAdmin: effective.isPlatformAdmin,
      isReseller: effective.isReseller,
      isAffiliate: effective.isAffiliate,
      capsLoading,
      refreshCapabilities: loadCapabilities,
      signOut: async (opts) => {
        // signOutSafe, not the bare signOut: it takes the same auth lock a stalled
        // token refresh holds, so on a poisoned session the button would hang and
        // never reach the redirect below.
        await signOutSafe(2000, opts?.scope);
        // Clear app-owned client state so the next login starts clean — a stale
        // active_store_id or leftover session key otherwise renders a half-broken
        // dashboard (collapsed nav + spinning KPIs) after re-login. The full-page
        // redirect (not SPA navigate) forces a fresh document load, which also
        // pulls the current bundle instead of any stale cached chunks.
        try {
          localStorage.removeItem("active_store_id");
          sessionStorage.clear();
        } catch {
          /* private mode / quota — nothing to clean up */
        }
        const to = opts?.redirectTo;
        window.location.assign(
          to && to.startsWith("/") && !to.startsWith("//") ? to : loginPathFor(window.location.pathname),
        );
      },
    };
  }, [session, loading, caps, capsLoading, loadCapabilities]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
